import { TRAINING_GATES } from "./training.js";
import type { FactionId } from "./factions.js";
import type { MissionVariant, SectorWeather } from "./contracts.js";
import { FACTION_LOCATIONS, FACTION_OBSTACLES } from "./world-locations.js";
export { FACTION_LOCATIONS, WORLD_NPC_SPAWNS } from "./world-locations.js";
export type { FactionLocationDefinition, WorldNpcSpawn } from "./world-locations.js";

export const LEGACY_SECTOR_IDS = ["patrol", "wrecks", "frontier"] as const;
export type LegacySectorId = (typeof LEGACY_SECTOR_IDS)[number];
export const FACTION_SECTOR_IDS = [
  "concord-azure", "concord-kepler", "concord-beacons", "concord-aegis", "concord-harmony",
  "helion-iron", "helion-slag", "helion-forge", "helion-imperative", "helion-rift",
  "nomads-magpie", "nomads-graveyard", "nomads-mist", "nomads-shards", "nomads-last-chance",
] as const;
export type FactionSectorId = (typeof FACTION_SECTOR_IDS)[number];
export const SECTOR_IDS = [...LEGACY_SECTOR_IDS, ...FACTION_SECTOR_IDS, "training"] as const;
export type SectorId = (typeof SECTOR_IDS)[number];
export interface RegionDefinition {
  id: SectorId;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  weather: SectorWeather;
  variant: MissionVariant;
  factionId: FactionId | null;
  pvpAllowed: boolean;
  description: string;
  resourceSummary: string;
}
export interface GateDefinition {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  toSectorId: SectorId;
  destinationX: number;
  destinationY: number;
}

export const WORLD_LAYOUT = {
  width: 12800,
  height: 15200,
  station: { x: 420, y: 1100, radius: 150 },
};
const LEGACY_REGIONS: readonly RegionDefinition[] = [
  {
    id: "patrol",
    factionId: null, pvpAllowed: false, description: "Безопасная стартовая область со станцией «Меридиан» и астероидами. Единственные врата в дальнем углу ведут в общее поле обломков.", resourceSummary: "Добыча из ресурсного астероида и переработка груза на общей станции.",
    name: "Патрульный пояс",
    x: 0,
    y: 0,
    width: 3200,
    height: 2200,
    weather: "clear",
    variant: "standard",
  },
  {
    id: "wrecks",
    factionId: null, pvpAllowed: true, description: "Общее поле обломков с налётами, исследованием и групповыми боями. Здесь возможны атаки других фракций. При любой гибели весь груз выпадает в контейнерах.", resourceSummary: "Контейнеры оборудования, ресурсные астероиды, обломки охраны и скан-маяки.",
    name: "Поле обломков",
    x: 3200,
    y: 0,
    width: 3200,
    height: 2200,
    weather: "ion-storm",
    variant: "ambush",
  },
  {
    id: "frontier",
    factionId: null, pvpAllowed: true, description: "Общая спорная область с ценными перевозками, контрабандой и захватом ретранслятора. Чужие фракции могут атаковать.", resourceSummary: "Энергия, контрабандные сигналы, ресурсные астероиды и атакованный транспорт.",
    name: "Спорная зона",
    x: 6400,
    y: 0,
    width: 3200,
    height: 2200,
    weather: "debris-field",
    variant: "reinforced",
  },
];
const SHARED_GATES: readonly GateDefinition[] = [
  {
    id: "meridian-wrecks",
    name: "В поле обломков",
    x: 3020,
    y: 2020,
    radius: 100,
    toSectorId: "wrecks",
    destinationX: 3630,
    destinationY: 180,
  },
  {
    id: "wrecks-return",
    name: "К станции «Меридиан»",
    x: 3380,
    y: 180,
    radius: 100,
    toSectorId: "patrol",
    destinationX: 2770,
    destinationY: 2020,
  },
  {
    id: "wrecks-to-frontier",
    name: "В спорную зону",
    x: 6220,
    y: 180,
    radius: 100,
    toSectorId: "frontier",
    destinationX: 6830,
    destinationY: 180,
  },
  {
    id: "frontier-return",
    name: "В поле обломков",
    x: 6580,
    y: 180,
    radius: 100,
    toSectorId: "wrecks",
    destinationX: 5970,
    destinationY: 180,
  },
];
const LEGACY_OBSTACLES: Record<
  LegacySectorId,
  readonly { id: string; x: number; y: number; radius: number }[]
> = {
  patrol: [
    { id: "patrol-rock-1", x: 1320, y: 610, radius: 48 },
    { id: "patrol-rock-2", x: 1960, y: 1550, radius: 55 },
    { id: "patrol-rock-3", x: 2580, y: 400, radius: 45 },
    { id: "patrol-rock-4", x: 2860, y: 1800, radius: 60 },
  ],
  wrecks: [
    { id: "wrecks-rock-1", x: 4210, y: 750, radius: 70 },
    { id: "wrecks-rock-2", x: 4475, y: 1075, radius: 62 },
    { id: "wrecks-rock-3", x: 4300, y: 1500, radius: 70 },
    { id: "wrecks-rock-4", x: 4900, y: 450, radius: 60 },
    { id: "wrecks-rock-5", x: 4700, y: 800, radius: 67 },
    { id: "wrecks-rock-6", x: 4990, y: 1420, radius: 55 },
    { id: "wrecks-rock-7", x: 5175, y: 725, radius: 70 },
    { id: "wrecks-rock-8", x: 5400, y: 1770, radius: 65 },
    { id: "wrecks-rock-9", x: 5700, y: 1260, radius: 60 },
    { id: "wrecks-rock-10", x: 5940, y: 680, radius: 68 },
  ],
  frontier: [
    { id: "frontier-rock-1", x: 7230, y: 620, radius: 70 },
    { id: "frontier-rock-2", x: 7550, y: 1450, radius: 65 },
    { id: "frontier-rock-3", x: 7920, y: 800, radius: 68 },
    { id: "frontier-rock-4", x: 8110, y: 1290, radius: 60 },
    { id: "frontier-rock-5", x: 8420, y: 1030, radius: 70 },
    { id: "frontier-rock-6", x: 9190, y: 1410, radius: 65 },
    { id: "frontier-rock-7", x: 9350, y: 550, radius: 58 },
  ],
};

export const REGIONS: readonly RegionDefinition[] = [...LEGACY_REGIONS, ...FACTION_LOCATIONS, {
  id: "training", name: "Учебный сектор", factionId: null, pvpAllowed: false,
  x: 9600, y: 0, width: 3200, height: 2200, weather: "clear", variant: "standard",
  description: "Первый вылет: управление, бой и помощь транспорту. После обучения — односторонний переход на базу своей фракции.",
  resourceSummary: "Учебный транспорт и налётчик. Возвращение из открытого мира недоступно.",
}];
function factionObstacles(id: FactionSectorId): readonly { id: string; x: number; y: number; radius: number }[] {
  const definition = FACTION_OBSTACLES.find((item) => item.sectorId === id);
  if (!definition) throw new Error(`Missing obstacles for ${id}`);
  return definition.obstacles;
}
export const REGION_OBSTACLES: Readonly<Record<SectorId, readonly { id: string; x: number; y: number; radius: number }[]>> = {
  ...LEGACY_OBSTACLES,
  training: [],
  "concord-azure": factionObstacles("concord-azure"),
  "concord-kepler": factionObstacles("concord-kepler"),
  "concord-beacons": factionObstacles("concord-beacons"),
  "concord-aegis": factionObstacles("concord-aegis"),
  "concord-harmony": factionObstacles("concord-harmony"),
  "helion-iron": factionObstacles("helion-iron"),
  "helion-slag": factionObstacles("helion-slag"),
  "helion-forge": factionObstacles("helion-forge"),
  "helion-imperative": factionObstacles("helion-imperative"),
  "helion-rift": factionObstacles("helion-rift"),
  "nomads-magpie": factionObstacles("nomads-magpie"),
  "nomads-graveyard": factionObstacles("nomads-graveyard"),
  "nomads-mist": factionObstacles("nomads-mist"),
  "nomads-shards": factionObstacles("nomads-shards"),
  "nomads-last-chance": factionObstacles("nomads-last-chance"),
};
const GATE_CORNERS = {
  "north-west": { x: 180, y: 180, arrivalX: 430 },
  "north-east": { x: 3020, y: 180, arrivalX: 2770 },
  "south-west": { x: 180, y: 2020, arrivalX: 430 },
  "south-east": { x: 3020, y: 2020, arrivalX: 2770 },
};
type GateCorner = keyof typeof GATE_CORNERS;
function connection(fromId: SectorId, toId: SectorId, fromCorner: GateCorner, toCorner: GateCorner): GateDefinition[] {
  const from = REGIONS.find((region) => region.id === fromId);
  const to = REGIONS.find((region) => region.id === toId);
  if (!from || !to) throw new Error(`Missing gate region ${fromId}/${toId}`);
  const fromPort = GATE_CORNERS[fromCorner];
  const toPort = GATE_CORNERS[toCorner];
  return [
    { id: `${fromId}-to-${toId}`, name: `В область «${to.name}»`, x: from.x + fromPort.x, y: from.y + fromPort.y, radius: 100, toSectorId: to.id, destinationX: to.x + toPort.arrivalX, destinationY: to.y + toPort.y },
    { id: `${toId}-to-${fromId}`, name: `В область «${from.name}»`, x: to.x + toPort.x, y: to.y + toPort.y, radius: 100, toSectorId: from.id, destinationX: from.x + fromPort.arrivalX, destinationY: from.y + fromPort.y },
  ];
}
const NEW_CONNECTIONS: readonly (readonly [SectorId, SectorId, GateCorner, GateCorner])[] = [
  ["wrecks", "concord-azure", "south-west", "north-west"],
  ["concord-azure", "concord-kepler", "south-east", "north-west"],
  ["concord-kepler", "concord-beacons", "south-east", "north-west"],
  ["concord-beacons", "concord-aegis", "south-east", "north-west"],
  ["concord-aegis", "concord-harmony", "south-east", "north-east"],
  ["wrecks", "helion-iron", "south-east", "north-west"],
  ["helion-iron", "helion-slag", "south-east", "north-west"],
  ["helion-slag", "helion-forge", "south-east", "north-west"],
  ["helion-forge", "helion-imperative", "south-east", "north-west"],
  ["helion-imperative", "helion-rift", "south-east", "north-east"],
  ["frontier", "nomads-magpie", "south-east", "north-west"],
  ["nomads-magpie", "nomads-graveyard", "south-east", "north-west"],
  ["nomads-graveyard", "nomads-mist", "south-east", "north-west"],
  ["nomads-mist", "nomads-shards", "south-east", "north-west"],
  ["nomads-shards", "nomads-last-chance", "south-east", "north-east"],
  ["concord-beacons", "helion-forge", "north-east", "south-west"],
  ["helion-forge", "nomads-mist", "north-east", "south-west"],
];
export const GATES: readonly GateDefinition[] = [...SHARED_GATES, ...TRAINING_GATES, ...NEW_CONNECTIONS.flatMap(([from, to, fromCorner, toCorner]) => connection(from, to, fromCorner, toCorner))];
export function worldRegionAt(x: number, y: number): RegionDefinition | undefined {
  return REGIONS.find((region) => x >= region.x && x < region.x + region.width && y >= region.y && y < region.y + region.height);
}
